import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, errMsg } from "../../lib/api";
import { useAdmin } from "./AdminApp";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Field } from "../../components/admin/Fields";
import { useSection, Card } from "../../components/admin/Section";
import PasskeyCard from "../../components/admin/PasskeyCard";

function PasswordCard() {
  const { logout } = useAdmin();
  const [f, setF] = useState({ current_password: "", new_password: "", code: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", f); toast.success("Şifre değişti. Tüm oturumlar kapatıldı, yeniden giriş yapın."); logout(); }
    catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Card title="Şifre değiştir" sub="En az 10 karakter; büyük/küçük harf, rakam ve sembol. Değişimde tüm oturumlar kapanır." testid="security-password">
      <form onSubmit={submit} className="grid md:grid-cols-4 gap-3 items-end" autoComplete="off">
        <Field label="Mevcut şifre"><Input type="password" autoComplete="new-password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="current-password" /></Field>
        <Field label="Yeni şifre"><Input type="password" autoComplete="new-password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="new-password" /></Field>
        <Field label="2FA kodu (açıksa)"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="password-2fa-code" /></Field>
        <Button data-testid="change-password-submit">Değiştir</Button>
      </form>
    </Card>
  );
}

function TwoFASetup() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const start = async () => { try { setData((await api.post("/auth/2fa/setup")).data); } catch (e) { toast.error(errMsg(e)); } };
  const enable = async () => { try { setCodes((await api.post("/auth/2fa/enable", { code })).data.backup_codes); toast.success("2FA etkinleştirildi"); } catch (e) { toast.error(errMsg(e)); } };
  if (codes)
    return (
      <Card title="Yedek kodlarınız" sub="Güvenli bir yere kaydedin; her kod bir kez kullanılır." testid="security-2fa">
        <div className="grid grid-cols-4 gap-2 font-mono text-sm bg-stone-50 border rounded-lg p-4" data-testid="new-backup-codes">{codes.map((c) => <span key={c}>{c}</span>)}</div>
        <Button onClick={() => window.location.reload()} data-testid="finish-2fa-setup">Kaydettim</Button>
      </Card>
    );
  return (
    <Card title="İki adımlı doğrulama (Google Authenticator)" sub="Durum: Kapalı. Açtığınızda girişte e-posta ve şifreye ek olarak uygulamadaki kod istenir." testid="security-2fa">
      {!data ? <Button onClick={start} data-testid="start-2fa-setup">2FA'yı kur</Button> : (
        <div className="flex flex-wrap gap-6 items-center">
          <div className="bg-white border rounded-xl p-4 flex flex-col items-center gap-2">
            <QRCodeSVG value={data.otpauth_uri} size={160} data-testid="totp-qr" />
            <code className="text-[10px] break-all max-w-[180px]" data-testid="totp-secret">{data.secret}</code>
          </div>
          <div className="space-y-3">
            <Field label="Uygulamadaki 6 haneli kod"><Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="setup-2fa-code" /></Field>
            <Button onClick={enable} disabled={code.length !== 6} data-testid="enable-2fa-submit">Doğrula ve etkinleştir</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function TwoFACard() {
  const { user } = useAdmin();
  return user.totp_enabled ? <TwoFAManage /> : <TwoFASetup />;
}

function TwoFAManage() {
  const { user, logout } = useAdmin();
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [codes, setCodes] = useState(null);
  const regen = async () => { try { setCodes((await api.post("/auth/2fa/backup-codes", { code })).data.backup_codes); setCode(""); } catch (e) { toast.error(errMsg(e)); } };
  const disable = async () => {
    if (!window.confirm("2FA kapatılırsa bir sonraki girişte yeniden kurulum istenir. Devam edilsin mi?")) return;
    try { await api.post("/auth/2fa/disable", { password: pw, code }); toast.success("2FA kapatıldı"); logout(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="İki adımlı doğrulama (Google Authenticator)" sub={`Durum: ${user.totp_enabled ? "Etkin" : "Kapalı"} · Kalan yedek kod: ${user.backup_codes_left}`} testid="security-2fa">
      <div className="grid md:grid-cols-3 gap-3 items-end">
        <Field label="Authenticator kodu"><Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="security-2fa-code" /></Field>
        <Button variant="outline" onClick={regen} disabled={!code} data-testid="regen-backup-codes">Yeni yedek kodlar üret</Button>
      </div>
      {codes && <div className="grid grid-cols-4 gap-2 font-mono text-sm bg-stone-50 border rounded-lg p-4" data-testid="new-backup-codes">{codes.map((c) => <span key={c}>{c}</span>)}</div>}
      <div className="grid md:grid-cols-3 gap-3 items-end border-t pt-5">
        <Field label="Kapatmak için şifre"><Input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="disable-2fa-password" /></Field>
        <Button variant="destructive" onClick={disable} disabled={!pw || !code} data-testid="disable-2fa">2FA'yı kapat</Button>
      </div>
    </Card>
  );
}

function Sessions() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin", "sessions"], queryFn: () => api.get("/auth/sessions").then((r) => r.data) });
  const revoke = async (id) => { await api.delete(`/auth/sessions/${id}`); qc.invalidateQueries({ queryKey: ["admin", "sessions"] }); toast.success("Oturum kapatıldı"); };
  return (
    <Card title="Aktif oturumlar" testid="security-sessions">
      <div className="divide-y border rounded-lg text-sm">
        {data.map((s) => (
          <div key={s.id} className="flex items-center gap-3 px-4 py-3" data-testid={`session-row-${s.id}`}>
            <div className="flex-1 min-w-0"><p className="truncate">{s.ua || "Bilinmeyen cihaz"}</p><p className="text-xs text-stone-400">{s.ip} · son kullanım {new Date(s.last_used).toLocaleString("tr-TR")}</p></div>
            {s.current ? <span className="text-xs text-green-600 font-semibold">Bu cihaz</span> : <Button size="sm" variant="outline" onClick={() => revoke(s.id)} data-testid={`revoke-session-${s.id}`}>Kapat</Button>}
          </div>
        ))}
      </div>
    </Card>
  );
}

function History() {
  const { data = [] } = useQuery({ queryKey: ["admin", "login-history"], queryFn: () => api.get("/auth/login-history").then((r) => r.data) });
  return (
    <Card title="Giriş geçmişi" testid="security-history">
      <div className="max-h-72 overflow-y-auto divide-y border rounded-lg text-xs">
        {data.map((h) => (
          <div key={h.id} className="flex gap-3 px-4 py-2">
            <span className={`w-16 font-semibold ${h.ok ? "text-green-600" : "text-red-600"}`}>{h.ok ? "Başarılı" : "Hatalı"}</span>
            <span className="w-36">{new Date(h.at).toLocaleString("tr-TR")}</span><span className="w-28">{h.ip}</span>
            <span className="flex-1 truncate text-stone-400">{h.email} · {h.ua}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Lockout() {
  const { f, set, save, busy } = useSection("security");
  return (
    <Card title="Giriş kuralları" onSave={save} busy={busy} testid="security-lockout">
      <label className="flex items-center gap-3 text-sm"><Switch checked={!!f.require_2fa} onCheckedChange={set("require_2fa")} data-testid="require-2fa-switch" />2 adımlı doğrulamayı zorunlu tut (kurulu değilse girişte kurulum istenir)</label>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Kilit öncesi hatalı deneme"><Input type="number" min={3} max={20} value={f.max_attempts ?? 5} onChange={(e) => set("max_attempts")(Number(e.target.value))} data-testid="lockout-attempts" /></Field>
        <Field label="Kilit süresi (dakika)"><Input type="number" min={1} value={f.lock_minutes ?? 15} onChange={(e) => set("lock_minutes")(Number(e.target.value))} data-testid="lockout-minutes" /></Field>
      </div>
    </Card>
  );
}

export default function SecurityTab() {
  return <><PasswordCard /><TwoFACard /><PasskeyCard /><Sessions /><History /><Lockout /></>;
}
